import React from 'react'
import { CircularProgress, Tooltip } from '@mui/material'
import {
  Code as JsonIcon,
  PictureAsPdfOutlined as PdfIcon,
  Refresh as RefreshIcon,
  TableChartOutlined as XlsxIcon,
} from '@mui/icons-material'
import { api, Response } from '../../function/axios'
import { formatDate, getNow } from '../../function/date'
import { Subscriber } from '../../store/user/reducer'
import { SearchResult } from '../../store/searcher/reducer'
import {
  getSearchType,
  handleDownloadPdf,
  handleDownloadXlsxEmails,
  handleDownloadXlsxPhones,
} from '../subscriber/SubscriberRequests'
import sprite_ from '../../sass/sprite.module.sass'
import { SourceIcon } from '../source-icon/SourceIcon'
import l from '../../page/SubscribersPageV2.module.css'
import s from './SubscriberRequestsV2.module.css'

type Props = {
  data: Subscriber
}

type Row = SearchResult & { isDeleted?: boolean }

// Same endpoint, page size and sort param as the old SubscriberRequests
// (DataTable appends ?sort=<key><DIR>, default "dateDESC").
const LIMIT = 20
const IMAGE_TYPES = ['face_search', 'irbis_eye', 'photo_searcher']
const NAMED_SOURCE_TYPES = ['api_v2_x', 'api_v2_instagram', 'api_v2_linkedin']
const MAX_SOURCE_ICONS = 6
/* the sprite icon the list showed before the icon collections — what the Text collection keeps showing */
const SOCIAL_API_SOURCES = ['fb_api', 'instagram_api', 'vk_api', 'ok_api', 'x_api', 'linkedin_api', 'tiktok_api', 'blsk_api', 'webmii_social']
const sourceClass = (name: string) =>
  SOCIAL_API_SOURCES.includes(name) ? sprite_['social__small_' + name.replace('_api', '')] : sprite_['social__small_' + name]

// Dot color for the workflow pill, grouped like the user UI's Search history.
export function typeColor(type: string) {
  if (/phone|real_phone/.test(type)) return '#2D89F4'
  if (/email/.test(type)) return '#8B5CF6'
  if (/face|photo|irbis_eye|image/.test(type)) return '#0EA5A5'
  if (/name/.test(type)) return '#10B981'
  if (/ip_geo|ipgeo/.test(type)) return '#F59E0B'
  if (/combined_id|sn_api|irbis_|crolo|api_v2|tiktok|bluesky/.test(type)) return '#EC4899'
  if (/kyc|criminal|ssn|pep|court|crypto/.test(type)) return '#EF4444'
  return '#64748B'
}


function downloadJson(e: React.MouseEvent, row: Row) {
  e.stopPropagation()
  const blob = new Blob([JSON.stringify(row)], { type: 'application/json' })
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = `${row.type}_${getNow()}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(href)
}

function StatusCell({ row }: { row: Row }) {
  const status = (row as any).status as string
  const percentage = Number((row as any).percentage) || 0
  let label = 'In progress'
  let color = 'var(--primary)'
  let progress: number | null = percentage || null
  if (status === 'finished') {
    label = 'Finished'
    color = 'var(--ok)'
    progress = null
  } else if (status === 'failed') {
    label = 'Failed'
    color = 'var(--danger)'
    progress = null
  } else if (status === 'empty') {
    label = 'No results'
    color = 'var(--text3)'
    progress = null
  }
  return (
    <>
      <span className={l.status}>
        <span className={l.dot} style={{ background: color }} />
        {label}
      </span>
      {progress !== null && (
        <div className={s.progress}>
          <div className={s.track}><i style={{ width: `${Math.min(100, progress)}%` }} /></div>
          <span className={l.sub} style={{ margin: 0 }}>{Math.round(progress)}%</span>
        </div>
      )}
    </>
  )
}

function SourcesCell({ row }: { row: Row }) {
  const sources: Array<{ name: string; percentage?: number | null }> = Array.isArray((row as any).sources) ? (row as any).sources : []
  if (sources.length === 0) return <span className={l.muted}>—</span>
  const names = sources.map((el) => el.name).join(', ')
  if (NAMED_SOURCE_TYPES.includes(row.type as string)) {
    return (
      <div className={s.sources}>
        {sources.map((el, idx) => <span key={idx} className={s.srcText}>{el.name}</span>)}
      </div>
    )
  }
  const visible = sources.slice(0, MAX_SOURCE_ICONS)
  const rest = sources.length - visible.length
  return (
    <Tooltip title={names}>
      <div className={s.sources}>
        {visible.map((el, idx) => <SourceIcon key={idx} source={el.name} className={s.src} fallback={<span className={`${sourceClass(el.name)} ${s.src}`} />} />)}
        {rest > 0 && <span className={s.more}>+{rest}</span>}
      </div>
    </Tooltip>
  )
}

export function SubscriberRequestsV2({ data }: Props) {
  const [rows, setRows] = React.useState<Row[]>([])
  const [count, setCount] = React.useState<number | null>(null)
  const [page, setPage] = React.useState(1)
  const [order, setOrder] = React.useState<'DESC' | 'ASC'>('DESC')
  const [loading, setLoading] = React.useState(false)
  const [reloadKey, setReloadKey] = React.useState(0)

  React.useEffect(() => {
    if (!data.uid) return
    let cancelled = false
    setLoading(true)
    api({
      method: 'GET',
      url: `/request-monitor/userwithdeleted/${data.uid}?sort=date${order}`,
      params: { offset: (page - 1) * LIMIT, limit: LIMIT },
    })
      .then(({ data: res }: Response) => {
        if (cancelled) return
        // Same two response shapes fetchTableData accepts.
        if (Array.isArray(res)) {
          setRows(res)
          setCount(null)
        } else {
          setRows(Array.isArray(res?.list) ? res.list : [])
          setCount(typeof res?.count === 'number' ? res.count : null)
        }
      })
      .catch(() => {
        if (!cancelled) setRows([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [data.uid, page, order, reloadKey])

  const total = count ?? rows.length
  const pageCount = Math.max(1, Math.ceil(total / LIMIT))
  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1
  const to = Math.min(page * LIMIT, total)

  const openResult = (row: Row) => {
    window.open(`/result_v2/${row.id}?subscriber=${data.id}`, '_blank', 'noopener,noreferrer')
  }

  const pageButtons = React.useMemo(() => {
    const pages: Array<number | '…'> = [1]
    for (let p = Math.max(2, page - 1); p <= Math.min(pageCount - 1, page + 1); p++) {
      if (p - (pages[pages.length - 1] as number) > 1) pages.push('…')
      pages.push(p)
    }
    if (pageCount > 1) {
      if (pageCount - (pages[pages.length - 1] as number) > 1) pages.push('…')
      pages.push(pageCount)
    }
    return pages
  }, [page, pageCount])

  return (
    <>
      <div className={s.head}>
        <h3>Searches</h3>
        <span className={s.hint}>· {total.toLocaleString()} results · including deleted</span>
        <span className={s.spacer} />
        <div className={s.seg}>
          <button className={order === 'DESC' ? s.on : ''} onClick={() => { setOrder('DESC'); setPage(1) }}>Newest</button>
          <button className={order === 'ASC' ? s.on : ''} onClick={() => { setOrder('ASC'); setPage(1) }}>Oldest</button>
        </div>
        <Tooltip title="Refresh">
          <button className={s.iconBtn} onClick={() => setReloadKey((k) => k + 1)} aria-label="Refresh">
            <RefreshIcon sx={{ fontSize: 16 }} />
          </button>
        </Tooltip>
      </div>

      <div className={l.scroll}>
        <table className={l.table} style={{ minWidth: 860 }}>
          <thead>
            <tr>
              <th style={{ paddingLeft: 16 }}>Criteria</th>
              <th>Workflow</th>
              <th>Date</th>
              <th>Status</th>
              <th>Sources</th>
              <th>Label</th>
              <th style={{ textAlign: 'right', paddingRight: 16 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const type = row.type as string
              const criteria = String((row as any).criteria ?? '')
              const finished = (row as any).status === 'finished'
              return (
                <tr key={row.id} onClick={() => openResult(row)}>
                  <td style={{ paddingLeft: 16 }}>
                    {IMAGE_TYPES.includes(type) ? (
                      <img className={s.thumb} src={criteria} alt="" />
                    ) : (
                      <div className={s.criteria} title={criteria}>{criteria || '—'}</div>
                    )}
                  </td>
                  <td>
                    <span className={l.pill} style={{ maxWidth: 220 }} title={getSearchType(row.type) || type}>
                      <span className={l.dot} style={{ background: typeColor(type) }} />
                      {getSearchType(row.type) || type}
                    </span>
                  </td>
                  <td className={l.nowrap}>
                    {formatDate((row as any).createdTime, 'dd/MM/yyyy')}
                    <span className={l.sub}>{formatDate((row as any).createdTime, 'HH:mm:ss')}</span>
                  </td>
                  <td><StatusCell row={row} /></td>
                  <td><SourcesCell row={row} /></td>
                  <td>{row.isDeleted ? <span className={s.deleted}>DELETED</span> : null}</td>
                  <td style={{ paddingRight: 16 }} onClick={(e) => e.stopPropagation()}>
                    <div className={s.actions}>
                      {type === 'profiler' && (
                        <Tooltip title="Download PDF">
                          <span>
                            <button className={s.act} disabled={!finished} onClick={(e) => handleDownloadPdf(e, row)}>
                              <PdfIcon sx={{ fontSize: 17 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {type === 'phone_list' && (
                        <Tooltip title="Download XLSX">
                          <span>
                            <button className={s.act} disabled={!finished} onClick={(e) => handleDownloadXlsxPhones(e, row)}>
                              <XlsxIcon sx={{ fontSize: 17 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {type === 'email_list' && (
                        <Tooltip title="Download XLSX">
                          <span>
                            <button className={s.act} disabled={!finished} onClick={(e) => handleDownloadXlsxEmails(e, row)}>
                              <XlsxIcon sx={{ fontSize: 17 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Download JSON">
                        <button className={s.act} onClick={(e) => downloadJson(e, row)}>
                          <JsonIcon sx={{ fontSize: 17 }} />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <div className={l.empty}>No searches yet</div>}
        {loading && (
          <div className={l.loading}>
            <CircularProgress size={28} />
          </div>
        )}
      </div>

      <div className={l.foot}>
        <span>{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
        <div className={l.pager}>
          <button className={l.pg} disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
          {pageButtons.map((p, idx) =>
            p === '…' ? (
              <button key={`gap${idx}`} className={l.pg} disabled>…</button>
            ) : (
              <button key={p} className={`${l.pg} ${p === page ? l.pgOn : ''}`} onClick={() => setPage(p)}>{p}</button>
            ),
          )}
          <button className={l.pg} disabled={page >= pageCount} onClick={() => setPage(page + 1)}>›</button>
        </div>
      </div>
    </>
  )
}
